import { TournamentSystem, ChessVariant, RatingType, TeamScoringUnit, TeamMode, ControlType, CompetitionFormat } from '@/services/utils/enums';
import IconBullet from '@/components/layout/icons/Bullet/Bullet.vue';
import IconBlitz from '@/components/layout/icons/Blitz/Blitz.vue';
import IconRapid from '@/components/layout/icons/Rapid/Rapid.vue';
import IconClassic from '@/components/layout/icons/Classic/Classic.vue';
import ServiceLogo from '@/assets/bar-chess-manager-logo.webp';

// 🔧 Global configuration file

// Источник публичных данных при запуске: 'db' или 'mock'. Меню меняет его до перезагрузки.
export const DATA_SOURCE = 'db';

// Движок по умолчанию при создании турнира: 'echecs' или 'legacy'.
// Фиксированные команды используют собственную жеребьёвку независимо от флага.
export const SWISS_PAIRING_ENGINE = 'echecs';
export const ROUND_ROBIN_PAIRING_ENGINE = 'echecs';

// Валюты, доступные для выбора в бухгалтерии (коды ISO 4217).
export const ACCOUNTING_CURRENCIES = ['GEL', 'EUR', 'USD', 'PLN'];

export const LOGGING = {
  GLOBAL_DEBUG_LOG: true,
  ALWAYS_LOG_ERROR: true,
  NET_SERVICES_LOG: true, // модульные переключатели для сетевых сервисов
  AUTH_LOG: true,
  MODELS_LOG: true,
  FUNC_SERVICES_LOG: true,
  ADMINTOOLS_LOG: true,
};

// Service brand (used outside club context)
export const SERVICE_BRAND = {
  logo: ServiceLogo,
  exportDomain: 'bar-chess.sharkdevelop.com',
  mainColor: '#fff7ea',
  highlightColor: '#ff9f2e',
  short: { left: 'Bar', highlight: 'Chess', right: '' },
  full: { left: 'Bar', highlight: 'Chess Manager', right: '' },
};

export const MAX_ROWS_PER_EXPORT_CARD = 10;

// Размеры иконок в PNG-экспорте турнирной таблицы.
// Значения можно настраивать отдельно для каждого варианта шахмат и контроля.
export const STANDINGS_EXPORT_ICON_SIZES = {
  chessVariants: {
    [ChessVariant.CLASSIC]: 18,
    [ChessVariant.CHESS960]: 17,
    [ChessVariant.BUGHOUSE]: 19,
    [ChessVariant.DOUBLES_2X2]: 20,
    [ChessVariant.HAND_AND_BRAIN]: 18,
  },
  controls: {
    [ControlType.BULLET]: 18,
    [ControlType.BLITZ]: 19,
    [ControlType.RAPID]: 22,
    [ControlType.CLASSIC]: 19,
  },
  fallback: 18,
};

// pdd-chess-frontend/src/config.js
export const DEFAULTS = {
  general: {
    system: TournamentSystem.SWISS, // или 'swiss'
    rated: true,
    hasEntryFee: false,
    acceptsSubscriptions: true,
    time: '19:30',
    competitionFormat: CompetitionFormat.INDIVIDUAL,
  },
  participants: {
    teamMode: TeamMode.RANDOM,
    participantLimit: null,
  },
  swiss: {
    rounds: 5,               // количество раундов по умолчанию
    timeMinutes: 7,         // минуты контроля
    increment: 5,            // инкремент в секундах
    tieBreakCoefficient: 'buchholz', // коэффициент тай-брейка
    byeResult: 'win',       // 'win' | 'draw' | 'loss'
  },
  elimination: {
    // базовые
    bracketsType: 'one',     // 'one' | 'top-bottom'
    sharedTime: true,

    // контроль времени (используются, если sharedTime === false)
    timeMinutes: 7,
    increment: 5,

    // условия победы
    winCondition: 'games',          // 'games' | 'score'
    gamesToWin: 1,
    pointsToWin: 1.5,
    teamScoringUnit: TeamScoringUnit.POINTS,
    tieBreaker: 'armageddon',       // 'armageddon' | 'black' | ... (по проекту)
    colorRule: 'random',            // 'random' | 'leaders-white' | 'leaders-black'

    // режим «одна сетка»
    oneBracketParticipantsMode: 'limit', // 'all' | 'limit'
    oneBracketLimit: 8,

    // режим «верх-низ»
    topBottomParticipantsMode: 'manual', // 'split' | 'manual'
    participantsTop: 4,
    participantsBottom: 4,
    remainingGoesToSecond: false,       // если true — все остальные во вторую сетку
  },
  roundRobin: {
    cycles: 1,                 // 1 круг — каждый с каждым один раз
    timeMinutes: 7,            // минуты контроля по умолчанию
    increment: 5,              // инкремент, сек
    tieBreakCoefficient: 'buchholz', // или нужный вам коэффициент
    randomizeLastOddCycleColors: true,
  },
};

export const TOURNAMENT_SYSTEM_BADGE_CLASS_MAP = {
  [TournamentSystem.MIXED]: 'badge-orange',
  [TournamentSystem.SWISS]: 'badge-turq',
  [TournamentSystem.ELIMINATION]: 'badge-green',
  [TournamentSystem.ROUND_ROBIN]: 'badge-purple'
};

export const HOMEPAGE_WIDGET_CONFIG = {
  clubsLimit: 10,
  tournamentsLimit: 10,
  latestResultsLimit: 3,
  ratingMoversLimit: 8,
  ratingMoversPeriodDays: 30,
  performersLimit: 8,
  performersPeriodDays: 30,
};

export const CHANGELOG_UNREAD_PERIOD_DAYS = 7;

function resolveDevApiUrl() {
  let raw = import.meta.env.VITE_API_URL || 'http://localhost:1337';

  try {
    const u = new URL(raw.replace(/\/+$/, '')); // убираем хвостовые /
    const isEnvLocal = ['localhost', '127.0.0.1'].includes(u.hostname);
    const isPageLocal = ['localhost', '127.0.0.1'].includes(location.hostname);

    // Только в dev: если env = localhost, а страница открыта по IP → подменяем хост
    if (isDev() && isEnvLocal && !isPageLocal) {
      u.hostname = location.hostname;
    }

    return u.toString().replace(/\/+$/, ''); // финально убираем хвостовой /
  } catch {
    // fallback если env битый
    return `${location.protocol}//${location.hostname}:1337`;
  }
}

function isDev() {
  return import.meta.env.DEV === true;
}

export function isDebug() {
  return import.meta.env.VITE_DEBUG === 'true';
}

export const RATING_CONTROL_ICON_MAP = {
  elo_t: {
    bullet: IconBullet,
    blitz: IconBlitz,
    rapid: IconRapid,
    classic: IconClassic,
  },
  elo_960: {
    bullet: IconBullet,
    blitz: IconBlitz,
    rapid: IconRapid,
    classic: IconClassic,
  },
  elo_marseillais: {
    bullet: IconBullet,
    blitz: IconBlitz,
    rapid: IconRapid,
    classic: IconClassic,
  },
  elo_bughouse: {
    bullet: IconBullet,
    blitz: IconBlitz,
    rapid: IconRapid,
    classic: IconClassic,
  },
  elo_doubles_2x2: {
    bullet: IconBullet,
    blitz: IconBlitz,
    rapid: IconRapid,
    classic: IconClassic,
  },
  elo_hand_and_brain: {
    bullet: IconBullet,
    blitz: IconBlitz,
    rapid: IconRapid,
    classic: IconClassic,
  },
};

export const VARIANT_RATING_MAP = {
  [ChessVariant.CLASSIC]: RatingType.ELO_T,
  [ChessVariant.CHESS960]: RatingType.ELO_960,
  [ChessVariant.MARSEILLAIS]: RatingType.ELO_MARSEILLAIS,
  [ChessVariant.BUGHOUSE]: RatingType.ELO_BUGHOUSE,
  [ChessVariant.DOUBLES_2X2]: RatingType.ELO_DOUBLES_2X2,
  [ChessVariant.HAND_AND_BRAIN]: RatingType.ELO_HAND_AND_BRAIN,
};

export const VITE_API_BASE_URL = isDev() ? `${resolveDevApiUrl()}/api` : `${import.meta.env.VITE_API_URL}/api`;

// Base URL without /api — used for media (uploads)
export const VITE_BASE_URL = isDev()
  ? resolveDevApiUrl()
  : import.meta.env.VITE_API_URL;

export const NAV_OFFSET = 90;
